import React from "react";
import { motion } from "framer-motion";
import { BriefcaseIcon } from "@heroicons/react/24/outline";
import { Experience, formatDateRange } from "../../firebase/experienceService";

interface ExperienceTimelineProps {
  experiences: Experience[];
  /** Limit how many roles to show (homepage shows the most recent few). */
  limit?: number;
}

/**
 * Compact, scannable career timeline. Each row shows company, role, dates,
 * a "Present" badge for the current role, and condensed impact bullets.
 */
const ExperienceTimeline: React.FC<ExperienceTimelineProps> = ({
  experiences,
  limit,
}) => {
  const items = typeof limit === "number" ? experiences.slice(0, limit) : experiences;

  if (items.length === 0) return null;

  return (
    <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-3 md:ml-4">
      {items.map((exp, index) => {
        const isCurrent = !exp.endDate || exp.endDate.trim() === "";
        return (
          <motion.li
            key={exp.id || index}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            className="relative ml-6 md:ml-8 pb-10 last:pb-0"
          >
            {/* Node */}
            <span className="absolute -left-[34px] md:-left-[42px] flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40 ring-4 ring-white dark:ring-gray-800">
              {exp.companyLogo ? (
                <img
                  src={exp.companyLogo}
                  alt={exp.company}
                  className="h-5 w-5 rounded-full object-cover"
                />
              ) : (
                <BriefcaseIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              )}
            </span>

            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                {exp.position}
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {formatDateRange(exp.startDate, exp.endDate)}
                </span>
                {isCurrent && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Present
                  </span>
                )}
              </div>
            </div>

            <p className="mt-0.5 text-base font-semibold text-primary-600 dark:text-primary-400">
              {exp.company}
            </p>

            {exp.descriptions && exp.descriptions.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {exp.descriptions.slice(0, 3).map((desc, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400"
                  >
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary-400" />
                    <span>{desc}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.li>
        );
      })}
    </ol>
  );
};

export default ExperienceTimeline;
